import { useCallback, useEffect, useRef, useState } from "react";
import type { OutboundVoiceRecorderCapability } from "./outbound-voice-recorder-capability.js";
import { useLiveRecordingWaveform } from "./use-live-recording-waveform.js";

export type ComposerVoiceRecorderPhase = "idle" | "recording" | "preview";

export type ComposerVoiceRecording = {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  previewUrl: string;
};

type UseComposerVoiceRecorderResult = {
  phase: ComposerVoiceRecorderPhase;
  elapsedMs: number;
  recording: ComposerVoiceRecording | null;
  permissionDenied: boolean;
  liveWaveformBarHeights: readonly number[] | null;
  liveWaveformProgress: number;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  discardRecording: () => void;
  resetRecorder: () => void;
};

function stopMediaStream(stream: MediaStream | null): void {
  if (!stream) {
    return;
  }
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

export function useComposerVoiceRecorder(input: {
  capability: OutboundVoiceRecorderCapability;
  disabled: boolean;
}): UseComposerVoiceRecorderResult {
  const [phase, setPhase] = useState<ComposerVoiceRecorderPhase>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recording, setRecording] = useState<ComposerVoiceRecording | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const ignoreStopRef = useRef(false);

  const resetRecorder = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    recorderRef.current?.stop();
    recorderRef.current = null;
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    setActiveStream(null);
    chunksRef.current = [];
    startedAtRef.current = null;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setElapsedMs(0);
    setRecording(null);
    setPhase("idle");
    setPermissionDenied(false);
  }, []);

  useEffect(() => () => resetRecorder(), [resetRecorder]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }
    recorder.stop();
  }, []);

  const startRecording = useCallback(async () => {
    if (input.disabled || !input.capability.supported || !input.capability.mimeType) {
      return;
    }
    resetRecorder();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setActiveStream(stream);
      const recorder = new MediaRecorder(stream, { mimeType: input.capability.mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          const totalBytes = chunksRef.current.reduce(
            (sum, chunk) => sum + (chunk instanceof Blob ? chunk.size : 0),
            0,
          );
          if (totalBytes >= input.capability.maxBytes) {
            stopRecording();
          }
        }
      });

      recorder.addEventListener("stop", () => {
        if (timerRef.current != null) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        stopMediaStream(streamRef.current);
        streamRef.current = null;
        setActiveStream(null);
        recorderRef.current = null;
        if (ignoreStopRef.current) {
          ignoreStopRef.current = false;
          chunksRef.current = [];
          startedAtRef.current = null;
          setRecording(null);
          setElapsedMs(0);
          setPhase("idle");
          return;
        }

        const blob = new Blob(chunksRef.current, { type: input.capability.mimeType! });
        chunksRef.current = [];
        const durationMs =
          startedAtRef.current != null ? Math.max(0, Date.now() - startedAtRef.current) : 0;
        startedAtRef.current = null;

        if (blob.size <= 0) {
          setPhase("idle");
          setElapsedMs(0);
          return;
        }

        const previewUrl = URL.createObjectURL(blob);
        previewUrlRef.current = previewUrl;
        setRecording({
          blob,
          mimeType: input.capability.mimeType!,
          durationMs,
          previewUrl,
        });
        setPhase("preview");
      });

      recorder.start(250);
      startedAtRef.current = Date.now();
      setPhase("recording");
      setPermissionDenied(false);
      timerRef.current = window.setInterval(() => {
        if (startedAtRef.current == null) {
          return;
        }
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 100);
    } catch (error) {
      resetRecorder();
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        setPermissionDenied(true);
      }
    }
  }, [input.capability, input.disabled, resetRecorder, stopRecording]);

  const discardRecording = useCallback(() => {
    ignoreStopRef.current = true;
    chunksRef.current = [];
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      ignoreStopRef.current = false;
    }
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    setActiveStream(null);
    startedAtRef.current = null;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setElapsedMs(0);
    setRecording(null);
    setPhase("idle");
  }, []);

  const liveWaveform = useLiveRecordingWaveform({
    stream: activeStream,
    active: phase === "recording",
    elapsedMs,
  });

  return {
    phase,
    elapsedMs,
    recording,
    permissionDenied,
    liveWaveformBarHeights: liveWaveform.barHeights,
    liveWaveformProgress: liveWaveform.progress,
    startRecording,
    stopRecording,
    discardRecording,
    resetRecorder,
  };
}
